// [<agent>] > [Runs] > [Scheduled] | [Conversations].
// docs/Y2026-Q3/design-portfolio-daily-runs.md section 12.
import { ProjectFilter, TypeFilter } from "../components/ConversationFilters";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import {
  useAgentRuns,
  type AgentRun,
  useDayManifest,
  type ManifestDocument,
} from "../api/hooks/useAgentPortfolio";
import {
  useStartAgentChat,
  useStatefulAgents,
} from "../api/hooks/useStatefulAgents";

import { DateRangePicker, lastDays } from "../components/DateRangePicker";
import { RunSpendText } from "../components/RunView";
import { agentLabelFrom, runStarterLabel } from "../utils/runParticipants";
import { passRunTimeLabel } from "../utils/runStatus";
import { ConversationStarter } from "../components/ConversationStarter";
import { RunsTable } from "../components/RunsTable";
import { RunNowPanel } from "../components/RunNowPanel";
import { runPath, statefulRunPage } from "./learningAgentPaths";
import { waitingLabel } from "../utils/responseCounts";
import styles from "./StatefulAgentsRoute.module.css";
import { Chevron } from "../components/Chevron";

// Must carry the same facts as the agent's own brief (project_brief.py).
const DocumentItem = ({ doc }: { doc: ManifestDocument }) => {
  const direction = [
    doc.sent_by_us ? "sent by us" : null,
    doc.received ? "received" : null,
  ].filter(Boolean);
  return (
    <li>
      <span className={styles.state}>{doc.state}</span> {doc.name}
      <span className={styles.muted}>
        {doc.kind ? ` · ${doc.kind.replace(/_/g, " ")}` : ""}
        {direction.length > 0 ? ` · ${direction.join(" and ")}` : ""}
      </span>
      {doc.state_reason && (
        <div className={styles.muted}>{doc.state_reason}</div>
      )}
      {doc.after_message_id && (
        // As the agent is told: a part-read thread is not a new one.
        <div className={styles.muted}>
          picks up after an earlier message already read
        </div>
      )}
      {(doc.seen_at ?? []).map((line) => (
        <div key={line} className={styles.muted}>
          {line}
        </div>
      ))}
    </li>
  );
};

// A day's intake runs to hundreds of documents on a live project, so a long group is
// collapsed. Expanded only when it is short enough to read without scrolling past it -
// the point of the section is to be skimmed, and a 245-item list is not a skim.
const EXPANDED_BY_DEFAULT_BELOW = 10;

const DocumentGroup = ({
  title,
  documents,
}: {
  title: string;
  documents: readonly ManifestDocument[];
}) => {
  const [open, setOpen] = useState(
    documents.length < EXPANDED_BY_DEFAULT_BELOW,
  );
  if (documents.length === 0) {
    return null;
  }
  return (
    <div className={styles.docGroup}>
      <button
        type="button"
        className={styles.docGroupToggle}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <Chevron tree open={open} /> {title} ({documents.length})
      </button>
      {open && (
        <ul>
          {documents.map((doc) => (
            <DocumentItem key={doc.upload_id} doc={doc} />
          ))}
        </ul>
      )}
    </div>
  );
};

const DocumentGroups = ({
  documents,
}: {
  documents: readonly ManifestDocument[];
}) => {
  const byName = (a: ManifestDocument, b: ManifestDocument) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  const email = documents.filter((d) => d.kind === "email_thread").sort(byName);
  const globals = documents
    .filter((d) => d.kind === GLOBAL_FILE_KIND)
    .sort(byName);
  const files = documents
    .filter((d) => d.kind !== "email_thread" && d.kind !== GLOBAL_FILE_KIND)
    .sort(byName);
  return (
    <>
      <DocumentGroup title="Email" documents={email} />
      <DocumentGroup title="Files" documents={files} />
      <DocumentGroup
        title="Global files not yet read here"
        documents={globals}
      />
    </>
  );
};

// Must match GLOBAL_FILE_KIND in day_manifest.py.
const GLOBAL_FILE_KIND = "global_file";

const RUNS_DEFAULT_DAYS = 30;

// Whether the pass works this site and why - the plan's own answer (``work_reason``),
// never re-derived here. Keys must match SweepPlan.work_reasons.
const WORK_REASONS: Record<string, string> = {
  chosen: "chosen for this pass",
  arrivals: "new or updated uploads on this site",
  slack: "new Slack messages that may concern it",
  global: "a global file that applies to it changed since an agent's last pass",
  removed: "a document an agent's record cites was removed",
  document_clauses: "a document has clauses an agent has not assessed, or must recheck",
  first_pass: "an agent's first pass on this site",
  clocks: "a date or wait on it changed state",
  handed: "another agent handed it something",
  reviewed: "an operator review is waiting to be applied",
  retest: "a platform change asks for its record to be re-tested",
};

const SiteStatus = ({
  reason,
  globalOnly,
}: {
  reason: string | null;
  globalOnly: boolean;
}) => {
  if (reason === null) {
    return (
      <div className={styles.muted}>
        {globalOnly
          ? "not worked on this run - its unread global files are older than each agent's last pass here"
          : "nothing new - not included"}
      </div>
    );
  }
  // Nothing new arrived on a site worked for a clock, a handoff, a review, a re-test or
  // a removal, so it is marked: this list is not the whole of why the run works a site.
  const quiet =
    reason === "clocks" ||
    reason === "handed" ||
    reason === "reviewed" ||
    reason === "retest" ||
    reason === "removed";
  return (
    <div className={quiet ? styles.needsReview : styles.muted}>
      will be worked - {WORK_REASONS[reason] ?? reason}
    </div>
  );
};

const formatRunTime = (iso: string): string => {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  const time = when.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  const day = when.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  // The browser's zone, stated, so it is not misread as UTC.
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${time} on ${day} (${zone})`;
};

// "2 emails", or "" - this run's unsent emails. Decisions are shown in Next actions.
const stillOpen = (run: AgentRun): string => waitingLabel({ emails: run.waiting?.emails ?? 0 });

// Each specialist's weekly read across its projects' notes, which asks behavior
// questions where a lesson recurs (design-operator-questions-and-action-provenance.md 5.5).
const LearningReviews = ({
  agents,
  range,
}: {
  agents: readonly { agent: string; label: string }[];
  range: { from: string; to: string };
}) => {
  const runs = useAgentRuns(
    agents.length > 0 ? agents.map((a) => a.agent) : null,
    "learning_review",
    range,
  );
  const label = agentLabelFrom(agents);
  return (
    <>
      <h2 className={styles.pageSectionTitle}>Weekly learning reviews</h2>
      {runs.isLoading && <p className={styles.muted}>Loading reviews…</p>}
      {runs.error ? (
        <p className={styles.error}>Could not load the learning reviews.</p>
      ) : null}
      {runs.data &&
        (runs.data.runs.length === 0 ? (
          <p className={styles.muted}>
            No learning review in this window. Each specialist with a project
            gets one every Monday.
          </p>
        ) : (
          <div className={styles.runTableScroll}>
            <table className={styles.runTable}>
              <thead>
                <tr>
                  <th>Specialist</th>
                  <th>When</th>
                  <th>Spend</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.runs.map((run) => (
                  <tr key={run.run_id}>
                    <td>
                      <Link
                        className={styles.link}
                        to={runPath("scheduled", run.run_id)}
                      >
                        {label(run.agent ?? "")}
                      </Link>
                    </td>
                    <td>{new Date(run.created_at).toLocaleString("en-GB")}</td>
                    <td>
                      <RunSpendText usd={run.spend_usd} />
                    </td>
                    <td>{run.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
    </>
  );
};

export const AgentScheduledRunsTab = () => {
  const agentsQuery = useStatefulAgents();
  const roster = agentsQuery.data?.agents ?? [];
  // The project team's tab: its daily run is ONE run, led by the Manager.
  const manager = roster.find((a) => a.is_manager)?.agent ?? null;
  const projectAgents = roster.map((a) => a.agent);
  const [range, setRange] = useState(() => lastDays(RUNS_DEFAULT_DAYS));
  const manifest = useDayManifest(manager);
  // The Manager's team runs, and the specialists' own sweeps from before them.
  const runs = useAgentRuns(
    projectAgents.length > 0 ? projectAgents : null,
    "scheduled",
    range,
  );
  const nowMs = Date.now();

  const projects = manifest.data?.projects ?? [];
  // Sites with uploads of their own, not those moved only by an agent's first pass,
  // whose documents the count beside it leaves out.
  const movedCount = projects.filter(
    (p) => p.work_reason === "arrivals",
  ).length;
  // NOT re-derived as moved + clocks. `projects_to_work` IS the plan's own answer
  // and the server sends it; computing a second one here is the "surfaces that must
  // agree" defect, and it disagreed - the old sum counted the manifest's entries,
  // which were one per (specialist, project).
  const includedCount = manifest.data?.projects_to_work.length ?? 0;
  const globalCount = manifest.data?.global_file_count ?? 0;

  return (
    <div className={styles.tab}>
      <h2 className={styles.pageSectionTitle}>Scheduled run history</h2>
      <DateRangePicker
        range={range}
        setRange={setRange}
        defaultDays={RUNS_DEFAULT_DAYS}
      />
      {runs.isLoading && <p className={styles.muted}>Loading runs…</p>}
      {runs.error ? (
        <p className={styles.error}>Could not load this agent&apos;s runs.</p>
      ) : null}
      {runs.data &&
        (runs.data.runs.length === 0 ? (
          <p className={styles.muted}>
            No scheduled run yet. Turn on scheduled runs in a project&apos;s own
            settings - they are off until you do.
          </p>
        ) : (
          <div className={styles.runTableScroll}>
          <table className={styles.runTable}>
            <thead>
              <tr>
                <th>Run</th>
                <th>When</th>
                <th>Started by</th>
                <th>Run time</th>
                <th>Sites</th>
                <th>Spend</th>
                <th>Still open</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {runs.data.runs.map((run) => (
                <tr key={run.run_id}>
                  <td>
                    <Link
                      className={styles.link}
                      to={runPath("scheduled", run.run_id)}
                    >
                      {run.title || run.run_id}
                    </Link>
                  </td>
                  <td>{new Date(run.created_at).toLocaleString("en-GB")}</td>
                  <td>{runStarterLabel(run)}</td>
                  <td>{passRunTimeLabel(run, nowMs) ?? "-"}</td>
                  {/* Not message_count, which double-counts each site. */}
                  <td>{run.projects_worked}</td>
                  <td>
                    <RunSpendText usd={run.spend_usd} />
                  </td>
                  <td>{stillOpen(run) || "-"}</td>
                  <td>{run.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        ))}

      <LearningReviews agents={roster} range={range} />

      {/* The next run's plan, and the control to run it now, in one card. */}
      <section className={`${styles.pageCard} ${styles.nextRunCard}`}>
        <h2 className={styles.pageCardTitle}>
          {manifest.data?.next_run_at
            ? `Next scheduled run - ${formatRunTime(manifest.data.next_run_at)}`
            : "Next scheduled run"}
        </h2>

        {/* Two lists, each saying why its sites are on it. */}
        <h3 className={styles.sectionTitle}>Dates and waits that changed:</h3>
        <p className={styles.muted}>
          A date on a site&apos;s row came within two weeks, passed or fell due,
          or a wait went quiet, since the agent last worked it. Each is worked
          once, on the day it changes - not every day after.
        </p>
        {manifest.data && manifest.data.clock_changes.length === 0 && (
          <p className={styles.muted}>Nothing changed.</p>
        )}
        <ul className={styles.attention}>
          {(manifest.data?.clock_changes ?? []).map((change) => (
            <li
              key={`${change.agent}-${change.project_id}-${change.description}`}
            >
              <Link
                className={styles.link}
                to={`/projects/${encodeURIComponent(change.project_id)}`}
              >
                {change.project_name}
              </Link>{" "}
              - {change.label}: {change.description}
            </li>
          ))}
        </ul>

        <h3 className={styles.sectionTitle}>
          New or updated uploads, by site:
        </h3>
        <p className={styles.muted}>
          Shown before the run happens, so you can check it against what you
          know arrived. A site is worked when one of its own uploads is new or
          updated, a date or wait on it changed state, another agent handed it
          something, a platform change asks for its record to be re-tested, or a
          global file that applies to it - shared across a client&apos;s sites -
          changed or was added after an agent&apos;s last pass there. A global
          file older than that pass is listed until it has been read, but does
          not start a run on its own: each agent reads or acknowledges it the
          next time it works the site.
        </p>
        {manifest.isLoading && (
          <p className={styles.muted}>Loading the manifest…</p>
        )}
        {manifest.error ? (
          <p className={styles.error}>Could not load the manifest.</p>
        ) : null}
        {manifest.data &&
          (manifest.data.projects.length === 0 ? (
            <p className={styles.muted}>
              No site is enrolled with this agent yet. Turn on scheduled runs in
              a project&apos;s own settings - they are off until you do.
            </p>
          ) : (
            <>
              <p className={styles.muted}>
                {manifest.data.document_count} new or updated upload
                {manifest.data.document_count === 1 ? "" : "s"} on {movedCount}{" "}
                of {manifest.data.projects.length} site
                {manifest.data.projects.length === 1 ? "" : "s"}
                {globalCount > 0
                  ? `, plus ${globalCount} unread global file${
                      globalCount === 1 ? "" : "s"
                    }`
                  : ""}
                . {includedCount} will be worked in total.
              </p>
              {manifest.data.projects.map((project) => (
                <div
                  key={project.project_id}
                  className={styles.manifestProject}
                >
                  <Link
                    className={styles.link}
                    to={`/projects/${encodeURIComponent(project.project_id)}`}
                  >
                    {project.project_name}
                  </Link>{" "}
                  {project.first_pass.map((first) => (
                    <div key={first.agent} className={styles.muted}>
                      {first.label} - first pass on this site: {first.documents}{" "}
                      document
                      {first.documents === 1 ? "" : "s"} to triage, none of them
                      new to the site
                    </div>
                  ))}
                  {/* Every site says whether it is worked, so a listed global file is
                    not read as a reason to run. */}
                  <SiteStatus
                    reason={project.work_reason}
                    globalOnly={
                      project.documents.length > 0 &&
                      project.documents.every(
                        (d) => d.kind === GLOBAL_FILE_KIND,
                      )
                    }
                  />
                  {project.documents.length > 0 && (
                    <DocumentGroups documents={project.documents} />
                  )}
                </div>
              ))}
            </>
          ))}

        {/* Last, because it acts on both lists above. Unlike invoking the
          Lambda by hand, it keeps the enrolment and in-flight checks. */}
        <RunNowPanel
          agent={manager}
          pageLabel="team's Scheduled"
          // Nothing moved and no clock changed, so a pass left to pick its own
          // work would do none. Choosing sites below is the way past that, which
          // is why the picker is offered here and the button un-blocks with it.
          disabled={includedCount === 0}
          choosableSites
          autoPickedCount={includedCount}
          runHref={(runId) => runPath("scheduled", runId)}
          note={
            "Runs the pass now rather than at its scheduled time, first checking Gmail, " +
            "Drive and Slack for anything new. Left alone it works what the plan finds " +
            "then. What you write below is read on every site " +
            "the pass works, as a steer for today rather than a standing rule."
          }
          chooseNote={
            // What "auto-picked" means, next to the count rather than inside the
            // control - it mirrors SweepPlan.projects_to_work and the manifest
            // above, so keep the three in step.
            "Auto-picked means new or updated uploads on the site itself, a global " +
            "file that changed since an agent's last pass there, dates and waits that " +
            "changed state, open handoffs, and platform changes to re-test. " +
            "Choose sites to override that: they are then worked whether or not " +
            "anything arrived on them, and nothing else is."
          }
          placeholder="e.g. the DNO replied on Friday - read that before anything else"
        />
      </section>
    </div>
  );
};

export const AgentConversationsTab = () => {
  const navigate = useNavigate();
  const { user } = useKindeAuth();
  const agentsQuery = useStatefulAgents();
  const projectAgents = agentsQuery.data?.agents ?? [];
  const manager = projectAgents.find((a) => a.is_manager)?.agent ?? "";
  // Same default window as the Scheduled tab and a project's own list, so "the
  // last 30 days" means one thing whichever page an operator is standing on.
  const [range, setRange] = useState(() => lastDays(RUNS_DEFAULT_DAYS));
  // Both kinds: every conversation any of these agents took part in. The window
  // is applied SERVER-side - filtering a capped list here drops older rows.
  const [types, setTypes] = useState<string[]>([]);
  const [projectIds, setProjectIds] = useState<string[]>([]);
  const runs = useAgentRuns(
    projectAgents.length > 0 ? projectAgents.map((a) => a.agent) : null,
    "conversations",
    range,
    { types, projectIds },
  );
  // The Manager answers unless a call names a specialist.
  const start = useStartAgentChat(manager);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={styles.tab}>
      <ConversationStarter
        roster={projectAgents}
        withSiteScope
        isLoading={start.isLoading}
        error={error}
        placeholder="e.g. you got the next step owner wrong on LPL2 last Tuesday - check the run"
        onStart={(choice, started) => {
          setError(null);
          start.mutate(
            {
              content: choice.content,
              actor_email: user?.email ?? "",
              // Who answers, as of this submit. The hook's own default is the
              // Manager; an empty choice means exactly that.
              ...(choice.agent ? { agent: choice.agent } : {}),
              is_private: choice.isPrivate,
              ...choice.attachments,
              // Omitted rather than sent in full: absent is what the server
              // reads as unrestricted, and it is what an untouched control means.
              ...(choice.specialists !== null
                ? { specialists: choice.specialists }
                : {}),
              ...(choice.scopeProjectIds.length > 0
                ? { scope_project_ids: choice.scopeProjectIds }
                : {}),
            },
            {
              // Go TO the new conversation, so it is clear the message was sent.
              onSuccess: (launched) => {
                started();
                navigate(runPath("conversations", launched.run_id));
              },
              // Never silent.
              onError: (err) =>
                setError(err.message || "Could not start that conversation."),
            },
          );
        }}
      />

      <section className={styles.listCard}>
        <h3 className={styles.listHeading}>Conversations</h3>
        <div className={styles.filters}>
          <DateRangePicker
            range={range}
            setRange={setRange}
            defaultDays={RUNS_DEFAULT_DAYS}
          />
          <TypeFilter selected={types} onChange={setTypes} />
          <ProjectFilter selected={projectIds} onChange={setProjectIds} />
        </div>

        {/* Said, or a slow/failed read looks like "no conversations yet". */}
        {runs.isLoading && <p className={styles.muted}>Loading…</p>}
        {runs.isError && (
          <p className={styles.error}>
            Could not load this agent&apos;s conversations.
          </p>
        )}
        {runs.data && runs.data.runs.length === 0 && (
          <p className={styles.muted}>No conversations yet.</p>
        )}
        {runs.data && runs.data.runs.length > 0 && (
          <RunsTable
            runs={runs.data.runs}
            showProjects
            showType
            href={statefulRunPage}
            agentLabel={agentLabelFrom(projectAgents)}
            allowDelete
          />
        )}
        <p className={styles.muted}>
          Both kinds are listed: conversations about this agent, and the ones
          about a single site. A site&apos;s own tab shows only its own.
        </p>
      </section>
    </div>
  );
};
